/**
 * Module-level singleton for WebSocket mutation locks.
 *
 * Prevents WebSocket-triggered cache invalidation during local mutations
 * (e.g., angle upload/replace). Without this, a WebSocket event can trigger
 * a concept refetch before the uploaded file is available on CDN, causing
 * the browser to cache a 404 response (negative caching).
 *
 * Why a singleton? Same reason as realtime-sync.registry.ts — React hook
 * refs are isolated per component, but mutation locks must be checked by
 * useRealtimeSync (in WebSocketProvider) and set by upload components.
 */

import { wsLogger } from '@/shared/lib/logger';

// Key: "entity:id" or "entity:*", Value: unlock timestamp (ms)
const pendingMutations = new Map<string, number>();

/**
 * Set a mutation lock for an entity. While active, incoming WebSocket events
 * for this entity (and optionally specific id) will be silently dropped.
 *
 * @param entity - Entity type (e.g., "angles", "concepts")
 * @param durationMs - Lock duration in milliseconds
 * @param id - Optional entity ID. Use "*" or omit for entity-wide lock.
 */
export function setMutationLock(
  entity: string,
  durationMs: number,
  id: string = '*'
): void {
  const key = `${entity}:${id}`;
  pendingMutations.set(key, Date.now() + durationMs);
  wsLogger.debug('[MutationLock] Acquired:', key, `(${durationMs}ms)`);
}

/**
 * Remove a mutation lock immediately.
 */
export function clearMutationLock(entity: string, id: string = '*'): void {
  const key = `${entity}:${id}`;
  pendingMutations.delete(key);
  wsLogger.debug('[MutationLock] Released:', key);
}

/**
 * Check if a mutation lock is active for the given entity and optional id.
 */
export function isMutationLocked(entity: string, id?: string): boolean {
  const now = Date.now();

  // Check specific entity:id lock
  if (id) {
    const specificTime = pendingMutations.get(`${entity}:${id}`);
    if (specificTime && now < specificTime) {
      return true;
    }
    // Clean up expired specific lock
    if (specificTime && now >= specificTime) {
      pendingMutations.delete(`${entity}:${id}`);
    }
  }

  // Check entity-wide lock
  const entityTime = pendingMutations.get(`${entity}:*`);
  if (entityTime && now < entityTime) {
    return true;
  }
  // Clean up expired entity lock
  if (entityTime && now >= entityTime) {
    pendingMutations.delete(`${entity}:*`);
  }

  return false;
}
